import { effect, isSignal, Signal } from '@angular/core';
import { DefaultError, injectQuery, QueryKey } from '@ngneat/query';
import { DefinedInitialDataOptions, UndefinedInitialDataOptions } from '@ngneat/query/lib/query-options';
import { Result } from '@ngneat/query/lib/types';
import { DefinedQueryObserverResult, QueryObserverResult } from '@tanstack/query-core';

type UseQueryResult<T, TOptions> = Result<T> & {
    update: (options: TOptions) => void;
};

export function useQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TOptions = unknown,
>(
    options: TOptions | Signal<TOptions>,
    getOptions: (
        options: TOptions,
    ) => UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<QueryObserverResult<TData, TError>, TOptions>;
export function useQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TOptions = unknown,
>(
    options: TOptions | Signal<TOptions>,
    getOptions: (
        options: TOptions,
    ) => DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseQueryResult<DefinedQueryObserverResult<TData, TError>, TOptions>;
export function useQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TOptions = unknown,
>(
    options: TOptions | Signal<TOptions>,
    getOptions: (
        options: TOptions,
    ) => UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>
):
    | UseQueryResult<QueryObserverResult<TData, TError>, TOptions>
    | UseQueryResult<DefinedQueryObserverResult<TData, TError>, TOptions> {
    const query = injectQuery();
    const { result, result$, updateOptions } = query(
        getOptions(isSignal(options) ? (null as TOptions) : options)
    );

    if (isSignal(options)) {
        effect(() => {
            updateOptions(getOptions(options()));
        });
    }

    return {
        result,
        result$,
        updateOptions,
        update: (options: TOptions) => updateOptions(getOptions(options))
    };
}
